import React, { useEffect, useRef, useState } from "react";
import { Crosshair, MapPin, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Google Maps iframe-based picker.
// Interactive pin drop is not possible with the free iframe embed, so the
// UX is: (a) fetch the customer's current GPS, or (b) search an address and
// pick a suggestion. Both set lat/long and center the iframe on that point.
// A "Fine-tune in Google Maps" link opens the real Google Maps app so the
// customer can grab a more accurate pin and paste back the coordinates.
export default function MapPicker({ value, onChange, onAddressResolved }) {
  const lat = value?.latitude;
  const lng = value?.longitude;
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const resolve = async (la, ln, extra = null) => {
    onChange && onChange({ latitude: la, longitude: ln });
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${la}&lon=${ln}`, {
        headers: { "Accept-Language": "en" },
      });
      const d = await r.json();
      const merged = {
        ...(extra || {}),
        ...(d.address || {}),
        display_name: (extra && extra.display_name) || d.display_name,
        latitude: la, longitude: ln,
      };
      onAddressResolved && onAddressResolved(merged);
    } catch { /* ignore */ }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        resolve(pos.coords.latitude, pos.coords.longitude);
        toast.success("Location captured");
      },
      (err) => {
        setBusy(false);
        toast.error(err.code === 1 ? "Location permission denied" : "Couldn't get location");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const runSearch = (q) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(q)}&limit=6&countrycodes=in`, {
          headers: { "Accept-Language": "en" },
        });
        const d = await r.json();
        setSuggestions(d || []);
      } catch { setSuggestions([]); }
    }, 300);
  };

  const pick = (s) => {
    const la = parseFloat(s.lat), ln = parseFloat(s.lon);
    setQuery(s.display_name);
    setSuggestions([]);
    resolve(la, ln, { display_name: s.display_name });
  };

  const mapSrc = lat && lng
    ? `https://www.google.com/maps?q=${lat},${lng}&z=17&output=embed`
    : `https://www.google.com/maps?q=India&z=5&output=embed`;

  return (
    <div className="border hairline bg-white" data-testid="map-picker">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b hairline bg-cream-200 relative">
        <Search size={16} className="text-ink-500 shrink-0" />
        <input
          value={query}
          onChange={(e) => runSearch(e.target.value)}
          placeholder="Search a landmark, area, or address…"
          className="flex-1 min-w-[200px] bg-white border hairline px-3 py-2 outline-none text-sm"
          data-testid="map-search"
        />
        <button
          onClick={useMyLocation}
          disabled={busy}
          className="btn-primary-ink px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          data-testid="map-use-location"
        >
          <Crosshair size={12} /> {busy ? "Locating…" : "Use my current location"}
        </button>
        {suggestions.length > 0 && (
          <div className="absolute left-3 right-3 top-full mt-1 bg-white border hairline shadow-lg z-20 max-h-72 overflow-auto" data-testid="map-suggestions">
            {suggestions.map((s) => (
              <button
                key={s.place_id}
                onClick={() => pick(s)}
                className="block w-full text-left px-3 py-2 hover:bg-cream-200 text-xs text-ink-700 border-b hairline"
              >
                {s.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      <iframe
        title="Google Maps preview"
        src={mapSrc}
        className="w-full"
        style={{ height: 320, border: 0 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        data-testid="google-maps-iframe"
      />

      <div className="p-3 flex flex-wrap justify-between items-center gap-3 border-t hairline text-[11px]">
        <div className="text-ink-500">
          {lat && lng ? (
            <span>📍 <span className="font-mono">{lat.toFixed(5)}, {lng.toFixed(5)}</span></span>
          ) : (
            <span>Search an address or use your current location to drop a pin.</span>
          )}
        </div>
        {lat && lng && (
          <div className="flex gap-3">
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-gold-500 hover:text-ink-900"
              data-testid="map-open-google"
            >
              Open in Google Maps <ExternalLink size={11} />
            </a>
            <span className="inline-flex items-center gap-1 text-ink-500">
              <MapPin size={11} /> Location saved
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
